// Phase 9.7.9 — Multidate Activity Ingest page.
//
// Route: /tools/fnb-po/multidate-activity
//
// Flow:
//   1. User uploads 1–3 monthly CSVs → hits /admin/upload-multidate-activity
//   2. Preview response shows:
//        • Store mapping table (33 rows) with pre-selected dropdowns
//          + "Reset to recommendation" per row
//        • Aggregate stats (flips, top consumers, collision count)
//        • Expandable manual-override collision list (informational only)
//   3. Commit fires /admin/commit-multidate-activity with the (possibly
//      edited) mapping. Idempotent — a second commit on the same token
//      returns 409.

import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, detailFromError } from "@/lib/api";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertTriangle, ArrowLeft, CheckCircle2, ChevronDown, ChevronRight,
  RefreshCcw, Rocket, Upload,
} from "lucide-react";

const REASON_META = {
  store_code:   { label: "Store code",   tone: "text-emerald-700" },
  name_exact:   { label: "Name exact",   tone: "text-emerald-700" },
  recommended:  { label: "Recommended",  tone: "text-sky-700" },
  fuzzy:        { label: "Fuzzy",        tone: "text-amber-800" },
  needs_review: { label: "Needs review", tone: "text-rose-700" },
  unmatched:    { label: "Unmatched",    tone: "text-rose-700" },
};

function reasonBadge(reason) {
  const meta = REASON_META[reason] || { label: reason, tone: "text-muted-foreground" };
  return (
    <Badge
      variant="outline"
      className={`text-[10px] uppercase tracking-wider ${meta.tone}`}
      data-testid={`multidate-mapping-reason-${reason}`}
    >
      {meta.label}
    </Badge>
  );
}


function UploadZone({ files, onChoose, busy, progress }) {
  const label = (idx) => {
    const f = files[idx];
    if (!f) return `Month ${idx + 1} — click to choose CSV`;
    return `${f.name} · ${(f.size / 1024).toFixed(1)} KB`;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="multidate-upload-zone">
      {[0, 1, 2].map((idx) => (
        <label
          key={idx}
          className={
            "block cursor-pointer rounded-md border border-dashed border-border/80 " +
            "px-4 py-6 text-center text-sm transition-colors " +
            (busy
              ? "opacity-60 cursor-wait"
              : "hover:border-foreground/40 hover:bg-muted/30")
          }
          data-testid={`multidate-file-input-label-${idx + 1}`}
        >
          <input
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0] || null;
              onChoose(idx, f);
              e.target.value = "";
            }}
            data-testid={`multidate-file-input-${idx + 1}`}
          />
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
            {["June (Month 1)", "July (Month 2)", "August (Month 3)"][idx]}
          </div>
          <div className="text-xs">{label(idx)}</div>
        </label>
      ))}
      {busy && (
        <div className="md:col-span-3">
          <Progress value={progress} />
        </div>
      )}
    </div>
  );
}


function MappingRow({ row, locations, override, onOverride, onReset }) {
  const currentLid = override?.location_id ?? row.recommended_location_id ?? "";
  const changed = override && override.location_id !== row.recommended_location_id;

  return (
    <TableRow data-testid={`multidate-mapping-row-${row.file_store_code || row.file_store_name}`}>
      <TableCell className="text-xs">
        <div className="font-medium">{row.file_store_name}</div>
        <div className="text-[10px] text-muted-foreground font-mono">
          {row.file_store_code || "—"}
        </div>
      </TableCell>
      <TableCell>{reasonBadge(row.reason)}</TableCell>
      <TableCell className="text-[11px] text-muted-foreground tabular-nums">
        {row.confidence != null ? row.confidence.toFixed(0) : "—"}
      </TableCell>
      <TableCell>
        <Select
          value={currentLid || "__skip__"}
          onValueChange={(v) => onOverride(v === "__skip__" ? null : v)}
        >
          <SelectTrigger
            className="min-w-[280px] h-8 text-xs"
            data-testid={`multidate-mapping-select-${row.file_store_code || row.file_store_name}`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__skip__" className="text-xs italic">
              — Skip this store —
            </SelectItem>
            {row.top_candidates?.map((c) => (
              <SelectItem
                key={`${c.location_id}-top`}
                value={c.location_id}
                className="text-xs font-mono"
              >
                {c.location_id}  ·  {c.score.toFixed(0)}  ({c.reason})
              </SelectItem>
            ))}
            {/* Every other location — safe fallback if the top-3 doesn't have the desired one */}
            {locations
              .filter((l) => !row.top_candidates?.some((c) => c.location_id === l.location_id))
              .map((l) => (
                <SelectItem key={l.location_id} value={l.location_id} className="text-xs font-mono">
                  {l.location_id}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="w-32">
        {changed && (
          <Button
            variant="ghost"
            size="sm"
            className="text-[10px] h-6 px-2 gap-1"
            onClick={onReset}
            data-testid={`multidate-mapping-reset-${row.file_store_code || row.file_store_name}`}
          >
            <RefreshCcw className="w-2.5 h-2.5" />
            Reset
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}


function CollisionList({ collisions }) {
  const [expanded, setExpanded] = useState(false);
  if (!collisions?.length) return null;

  return (
    <Card className="border-amber-300 bg-amber-50/50" data-testid="multidate-collision-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-700" />
              <span data-testid="multidate-collision-count">{collisions.length}</span>{" "}
              manual-override collision{collisions.length === 1 ? "" : "s"}
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              Items with existing manual applicability locks that conflict with the new
              activity data. Commit will SKIP the flip for these — they remain blocked
              until you separately hit{" "}
              <code className="text-[10px] font-mono">/admin/applicability/release-manual-overrides</code>.
              Informational only, no acknowledgement required.
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-7 gap-1"
            onClick={() => setExpanded(!expanded)}
            data-testid="multidate-collision-toggle"
          >
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {expanded ? "Hide" : "Show"} list
          </Button>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0">
          <div className="max-h-72 overflow-auto rounded border border-amber-200 bg-white text-xs">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[10px]">SKU</TableHead>
                  <TableHead className="text-[10px]">Item</TableHead>
                  <TableHead className="text-[10px]">Location</TableHead>
                  <TableHead className="text-[10px]">Manual says</TableHead>
                  <TableHead className="text-[10px]">File says</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {collisions.map((c, i) => (
                  <TableRow key={`${c.item_sku}-${c.location_id}-${i}`}
                            data-testid={`multidate-collision-row-${i}`}>
                    <TableCell className="font-mono text-[10px]">{c.item_sku}</TableCell>
                    <TableCell className="text-[10px]">{c.item_name}</TableCell>
                    <TableCell className="font-mono text-[10px]">{c.location_id}</TableCell>
                    <TableCell className="text-[10px]">
                      {c.manual_says ? "active" : "inactive"}
                    </TableCell>
                    <TableCell className="text-[10px]">
                      {c.new_says ? "active" : "inactive"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      )}
    </Card>
  );
}


export default function FnbPoMultidateActivity() {
  const [files, setFiles] = useState([null, null, null]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [overrides, setOverrides] = useState({}); // {file_store_name: {location_id}}
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null);
  const [locations, setLocations] = useState([]);

  const chooseFile = (idx, f) => {
    const next = [...files];
    next[idx] = f;
    setFiles(next);
    setPreview(null);
    setError(null);
  };

  const canPreview = files.some((f) => f);

  const doPreview = useCallback(async () => {
    if (!canPreview) return;
    setBusy(true);
    setProgress(0);
    setError(null);
    setPreview(null);
    setCommitResult(null);
    setOverrides({});
    try {
      // Pull the full locations universe for the "all locations" fallback in the mapping select.
      const locResp = await api.get("/fnb/locations");
      setLocations(locResp.data || []);

      const fd = new FormData();
      files.forEach((f, idx) => {
        if (f) fd.append(`file_${idx + 1}`, f);
      });
      const res = await api.post("/fnb/admin/upload-multidate-activity", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
        timeout: 5 * 60 * 1000,
      });
      setPreview(res.data);
      toast.success("Preview ready — review the mapping before committing.");
    } catch (e) {
      const msg = detailFromError(e);
      setError(msg);
      toast.error(`Preview failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  }, [files, canPreview]);

  const setOverride = (name, location_id) => {
    setOverrides((prev) => ({ ...prev, [name]: { location_id } }));
  };
  const resetOverride = (name) => {
    setOverrides((prev) => {
      const { [name]: _drop, ...rest } = prev;
      return rest;
    });
  };

  const effectiveMapping = useMemo(() => {
    if (!preview) return [];
    return preview.store_mapping.map((row) => {
      const ov = overrides[row.file_store_name];
      return {
        file_store_name: row.file_store_name,
        location_id: ov ? ov.location_id : row.recommended_location_id,
      };
    });
  }, [preview, overrides]);

  const skippedCount = effectiveMapping.filter((r) => !r.location_id).length;

  const doCommit = async () => {
    if (!preview) return;
    setCommitting(true);
    setError(null);
    try {
      const res = await api.post("/fnb/admin/commit-multidate-activity", {
        preview_token: preview.preview_token,
        store_mapping: effectiveMapping,
      });
      setCommitResult(res.data);
      toast.success(
        `Wrote ${res.data.rows_written.toLocaleString()} rows · ${res.data.flips_applied} flips applied.`,
      );
    } catch (e) {
      const msg = detailFromError(e);
      setError(msg);
      toast.error(`Commit failed: ${msg}`);
    } finally {
      setCommitting(false);
    }
  };

  const stats = preview?.pair_stats || {};
  const committed = !!commitResult;

  return (
    <div className="space-y-6" data-testid="fnb-po-multidate-activity">
      <header className="flex items-center justify-between">
        <div className="space-y-1">
          <h1
            className="text-2xl font-semibold tracking-tight"
            data-testid="multidate-heading"
          >
            Multidate Activity Ingest
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Upload up to 3 monthly POS activity CSVs. Preview matches every file
            store to your <code className="text-xs">fb_locations</code>, aggregates the
            activity flags + sales across months, and stages a diff. Nothing is
            written until you commit.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm" data-testid="multidate-back-master-data">
            <Link to="/tools/fnb-po/master-data">
              <ArrowLeft className="w-4 h-4 mr-1" /> Master Data
            </Link>
          </Button>
        </div>
      </header>

      {/* Step 1 — file zone */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">1. Choose CSV files</CardTitle>
          <CardDescription className="text-xs">
            One CSV per month. Missing months are OK — the aggregator ORs
            activity across whatever you supply.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <UploadZone files={files} onChoose={chooseFile} busy={busy} progress={progress} />
          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              {files.filter(Boolean).length} of 3 files selected
            </div>
            <Button
              onClick={doPreview}
              disabled={!canPreview || busy}
              data-testid="multidate-preview-btn"
            >
              <Upload className="w-4 h-4 mr-1" />
              {busy ? "Uploading & parsing…" : "Preview"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive" data-testid="multidate-error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Step 2 — stats */}
      {preview && (
        <div
          className="grid grid-cols-2 md:grid-cols-6 gap-3"
          data-testid="multidate-stat-grid"
        >
          <StatTile label="Total pairs" value={stats.total_pairs} testid="stat-total-pairs" />
          <StatTile label="Matched" value={stats.matched_pairs} testid="stat-matched-pairs" />
          <StatTile label="Unmapped" value={stats.unmapped_pairs} testid="stat-unmapped-pairs" tone="text-rose-700" />
          <StatTile label="Flips → active" value={stats.flips_to_active} testid="stat-flips-active" tone="text-emerald-700" />
          <StatTile label="Flips → inactive" value={stats.flips_to_inactive} testid="stat-flips-inactive" tone="text-amber-700" />
          <StatTile label="Unchanged" value={stats.unchanged} testid="stat-unchanged" tone="text-muted-foreground" />
        </div>
      )}

      {/* Step 3 — collisions banner */}
      {preview?.manual_override_collisions && (
        <CollisionList collisions={preview.manual_override_collisions} />
      )}

      {/* Step 4 — store mapping */}
      {preview && (
        <Card data-testid="multidate-mapping-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">2. Store mapping</CardTitle>
                <CardDescription className="text-xs">
                  Every file store is pre-selected to our recommendation. Change or skip
                  any row before committing. {skippedCount > 0 && (
                    <span className="text-amber-800 ml-1">
                      · {skippedCount} store{skippedCount === 1 ? "" : "s"} will be skipped.
                    </span>
                  )}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <Table data-testid="multidate-mapping-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-64 text-[11px]">File store · code</TableHead>
                    <TableHead className="w-32 text-[11px]">Reason</TableHead>
                    <TableHead className="w-16 text-[11px] text-right">Score</TableHead>
                    <TableHead className="text-[11px]">Maps to</TableHead>
                    <TableHead className="w-32 text-[11px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.store_mapping.map((row) => (
                    <MappingRow
                      key={row.file_store_name}
                      row={row}
                      locations={locations}
                      override={overrides[row.file_store_name]}
                      onOverride={(lid) => setOverride(row.file_store_name, lid)}
                      onReset={() => resetOverride(row.file_store_name)}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 5 — top consumers */}
      {preview?.top_consumers?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Top 20 consumers (aggregated qty across 3 months)</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto rounded border border-border/60">
              <Table data-testid="multidate-top-consumers-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[11px]">SKU</TableHead>
                    <TableHead className="text-[11px]">Item</TableHead>
                    <TableHead className="text-[11px]">Location</TableHead>
                    <TableHead className="text-[11px] text-right">Total qty</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.top_consumers.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-[10px]">{r.item_sku}</TableCell>
                      <TableCell className="text-xs">{r.item_name}</TableCell>
                      <TableCell className="font-mono text-[10px]">{r.location_id}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs">
                        {r.total_sales_qty?.toLocaleString?.() ?? r.total_sales_qty}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 6 — commit */}
      {preview && !committed && (
        <div className="sticky bottom-4 z-40 flex justify-end">
          <Card className="border-emerald-300 bg-emerald-50/40 shadow-md" data-testid="multidate-commit-bar">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="text-xs text-muted-foreground">
                Ready to write up to{" "}
                <b>{stats.matched_pairs?.toLocaleString?.() ?? 0}</b>{" "}
                pairs · <b>{(stats.flips_to_active || 0) + (stats.flips_to_inactive || 0)}</b> flips
                {skippedCount > 0 && (
                  <span className="ml-1 text-amber-800">
                    · skipping {skippedCount} store{skippedCount === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              <Button
                onClick={doCommit}
                disabled={committing}
                data-testid="multidate-commit-btn"
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
              >
                <Rocket className="w-4 h-4 mr-1" />
                {committing ? "Committing…" : "Commit"}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Commit-success dialog */}
      <Dialog open={committed} onOpenChange={(v) => { if (!v) setCommitResult(null); }}>
        <DialogContent data-testid="multidate-commit-success">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" /> Committed
            </DialogTitle>
            <DialogDescription>
              Multidate activity applied at {commitResult?.committed_at?.slice(0, 19)}.
            </DialogDescription>
          </DialogHeader>
          {commitResult && (
            <div className="text-sm space-y-1 py-2">
              <Row label="Rows written" value={commitResult.rows_written} />
              <Row label="Activity flips applied" value={commitResult.flips_applied} />
              <Row label="Skipped by manual lock" value={commitResult.flips_skipped_by_lock} />
              <Row label="Consumption rates upserted" value={commitResult.rates_upserted} />
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button asChild variant="ghost" size="sm">
              <Link to="/tools/fnb-po/ordering-rules" data-testid="multidate-goto-ordering">
                Ordering Rules →
              </Link>
            </Button>
            <Button size="sm" onClick={() => { setCommitResult(null); setPreview(null); setFiles([null, null, null]); }}>
              Ingest another
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function StatTile({ label, value, testid, tone = "text-foreground" }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className={`text-xl font-semibold tabular-nums ${tone}`} data-testid={testid}>
          {value?.toLocaleString?.() ?? value ?? "—"}
        </div>
      </CardContent>
    </Card>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <b className="tabular-nums">{value?.toLocaleString?.() ?? value ?? 0}</b>
    </div>
  );
}
